import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  LogOut,
  Menu as MenuIcon,
  RefreshCw,
} from "lucide-react";
import Logo from "../assets/logoDashboard.png";
import NotificationCenter from "./NotificationCenter";
import ClockStatus from "./ClockStatus";
import {
  AGENT_BRAND,
  AGENT_INK,
  AGENT_LINE,
  AGENT_MUTED,
  AGENT_SURFACE,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
} from "./agentTheme";
import { clearSession } from "../utils/clearSession";

// Shared shell for every agent screen: fixed sidebar, sticky top bar, content
// well. Previously each page mounted the drawer itself and set its own padding,
// so the chrome drifted between screens -- this is the single place it lives.



const NAV_GROUPS = [
  {
    heading: "Main",
    items: [
      { label: "Dashboard", icon: LayoutDashboard, path: "/agent/dashboard" },
      { label: "My Attendance", icon: CalendarClock, path: "/agent/attendance" },
    ],
  },
  {
    heading: "Work",
    items: [
      { label: "Tickets", icon: LifeBuoy, path: "/agent/all-ticket" },
      { label: "Campaign Sync", icon: RefreshCw, path: "/agent/campaign-sync" },
      { label: "Campaign Monitor", icon: ListChecks, path: "/agent/campaign-monitor" },
    ],
  },
];

const initialsOf = (name) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("") || "A";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

/* ── Sidebar ─────────────────────────────────────────────────────────────── */
function SidebarContent({ collapsed, onNavigate, onToggleCollapse, showCollapse }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (path) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#FFFFFF",
        borderRight: `1px solid ${AGENT_LINE}`,
      }}
    >
      <Box
        sx={{
          height: 68,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 0 : 2.5,
          borderBottom: `1px solid ${AGENT_LINE}`,
          flexShrink: 0,
        }}
      >
        {collapsed ? (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: AGENT_BRAND,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 15,
            }}
          >
            JT
          </Box>
        ) : (
          <img src={Logo} alt="JustTawk" style={{ height: 30 }} />
        )}
        {showCollapse && !collapsed && (
          <IconButton size="small" onClick={onToggleCollapse} sx={{ color: AGENT_MUTED }}>
            <ChevronLeft size={18} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", py: 2, px: collapsed ? 1 : 1.5 }}>
        {NAV_GROUPS.map((group) => (
          <Box key={group.heading} sx={{ mb: 2.5 }}>
            {!collapsed && (
              <Typography
                sx={{
                  px: 1.5,
                  mb: 0.75,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#94A3B8",
                }}
              >
                {group.heading}
              </Typography>
            )}

            {group.items.map(({ label, icon: Icon, path }) => {
              const active = pathname === path || pathname.startsWith(`${path}/`);
              const button = (
                <Box
                  key={path}
                  onClick={() => go(path)}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: collapsed ? 0 : 1.5,
                    justifyContent: collapsed ? "center" : "flex-start",
                    height: 42,
                    mb: 0.5,
                    borderRadius: "10px",
                    cursor: "pointer",
                    color: active ? AGENT_BRAND : "#475569",
                    bgcolor: active ? "#EEF2FF" : "transparent",
                    fontWeight: active ? 700 : 500,
                    transition: "background-color .15s, color .15s",
                    "&:hover": { bgcolor: active ? "#E6ECFF" : "#F1F5F9" },
                    // Keka-style accent rail on the active item.
                    "&::before": active
                      ? {
                          content: '""',
                          position: "absolute",
                          left: collapsed ? 4 : 0,
                          top: 9,
                          bottom: 9,
                          width: 3,
                          borderRadius: 3,
                          bgcolor: AGENT_BRAND,
                        }
                      : undefined,
                  }}
                >
                  <Icon size={19} strokeWidth={active ? 2.4 : 2} />
                  {!collapsed && <Typography sx={{ fontSize: 14, fontWeight: "inherit" }}>{label}</Typography>}
                </Box>
              );

              return collapsed ? (
                <Tooltip key={path} title={label} placement="right">
                  {button}
                </Tooltip>
              ) : (
                button
              );
            })}
          </Box>
        ))}
      </Box>

      {showCollapse && collapsed && (
        <Box sx={{ display: "flex", justifyContent: "center", pb: 1 }}>
          <IconButton size="small" onClick={onToggleCollapse} sx={{ color: AGENT_MUTED }}>
            <ChevronRight size={18} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

/* ── Layout ──────────────────────────────────────────────────────────────── */
export default function AgentLayout({ title, subtitle, actions, children }) {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width:900px)");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("agentNavCollapsed") === "1"
  );
  const [anchorEl, setAnchorEl] = useState(null);

  const agentName = localStorage.getItem("agentName") || "Agent";
  const agentUserId = localStorage.getItem("agentUserId") || "";
  const initials = useMemo(() => initialsOf(agentName), [agentName]);

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      localStorage.setItem("agentNavCollapsed", prev ? "0" : "1");
      return !prev;
    });
  };

  const logout = () => {
    clearSession();
    navigate("/agent/login");
  };

  const railWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: AGENT_SURFACE }}>
      {isDesktop ? (
        <Box
          component="nav"
          sx={{
            width: railWidth,
            flexShrink: 0,
            position: "fixed",
            inset: "0 auto 0 0",
            zIndex: 1200,
            transition: "width .2s ease",
          }}
        >
          <SidebarContent
            collapsed={collapsed}
            showCollapse
            onToggleCollapse={toggleCollapse}
          />
        </Box>
      ) : (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: SIDEBAR_WIDTH, border: "none" } }}
        >
          <SidebarContent collapsed={false} onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          ml: isDesktop ? `${railWidth}px` : 0,
          transition: "margin-left .2s ease",
        }}
      >
        {/* ── Top bar ─────────────────────────────────────────────────── */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1100,
            minHeight: 68,
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: { xs: 1.5, md: 0 },
            bgcolor: "rgba(255,255,255,0.88)",
            backdropFilter: "blur(8px)",
            borderBottom: `1px solid ${AGENT_LINE}`,
          }}
        >
          {!isDesktop && (
            <IconButton onClick={() => setMobileOpen(true)} sx={{ color: AGENT_INK, ml: -1 }}>
              <MenuIcon size={22} />
            </IconButton>
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{ fontSize: { xs: 16, md: 19 }, fontWeight: 700, color: AGENT_INK, lineHeight: 1.25 }}
              noWrap
            >
              {title || `${greeting()}, ${agentName.split(" ")[0]}`}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 13, color: AGENT_MUTED }} noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 1.5 } }}>
            {actions}
            <ClockStatus />
            <NotificationCenter />

            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                pl: { xs: 0, md: 1.5 },
                ml: { xs: 0, md: 0.5 },
                borderLeft: { xs: "none", md: `1px solid ${AGENT_LINE}` },
                cursor: "pointer",
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: "#EEF2FF",
                  color: AGENT_BRAND,
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {initials}
              </Avatar>
              <Box sx={{ display: { xs: "none", md: "block" }, lineHeight: 1.2 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: AGENT_INK }} noWrap>
                  {agentName}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: AGENT_MUTED }} noWrap>
                  {agentUserId || "Agent"}
                </Typography>
              </Box>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: { mt: 1, minWidth: 190, borderRadius: "12px", boxShadow: "0 12px 32px rgba(15,23,42,.12)" },
              }}
            >
              <Box sx={{ px: 2, py: 1.25 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: AGENT_INK }}>
                  {agentName}
                </Typography>
                <Typography sx={{ fontSize: 12, color: AGENT_MUTED }}>{agentUserId}</Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  navigate("/agent/attendance");
                }}
                sx={{ fontSize: 14, gap: 1.5 }}
              >
                <CalendarClock size={16} /> My Attendance
              </MenuItem>
              <MenuItem onClick={logout} sx={{ fontSize: 14, gap: 1.5, color: "#DC2626" }}>
                <LogOut size={16} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box component="main" sx={{ p: { xs: 2, md: 3 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
